<template>
  <div class="d-flex justify-space-between mb-4">
    <v-card
      class="sensitive-data-wrapper mr-4 pa-2 pt-3"
      data-testid="dlp-settings"
      v-if="modelValue"
    >
      <v-card-text>
        <div class="subtitle1">
          {{ $t("endpointDataGovernance.privacySensitiveDataTab.title") }}
        </div>
        <div
          class="mb-4"
          v-html="$t('endpointDataGovernance.privacySensitiveDataTab.sensitiveDataSubtitle')"
        ></div>
        <div class="subtitle1 mb-2">
          {{ $t("endpointDataGovernance.privacySensitiveDataTab.recommendedDataTypes") }}
        </div>
        <v-checkbox
          v-for="recommendedDataType in recommendedDataTypes"
          :key="recommendedDataType"
          :disabled="actionNotAllowed"
          :data-testid="`dlp-${recommendedDataType}-checkbox`"
          v-model="localValue[recommendedDataType as keyof PrivacySensitiveDataSettings]"
          :label="$t(`userDataGovernanceSettings.monitoringTab.${recommendedDataType}`)"
        />
        <div class="subtitle1 mt-4 mb-2">
          {{ $t("endpointDataGovernance.privacySensitiveDataTab.otherDataTypes") }}
        </div>
        <span v-if="otherDataTypes.length === 0">
          {{ $t("endpointDataGovernance.privacySensitiveDataTab.noOtherDataTypes") }}
        </span>
        <v-checkbox
          v-for="otherDataType in otherDataTypes"
          :key="otherDataType"
          :disabled="actionNotAllowed"
          :data-testid="`dlp-${otherDataType}-checkbox`"
          v-model="localValue[otherDataType as keyof PrivacySensitiveDataSettings]"
          :label="$t(`userDataGovernanceSettings.monitoringTab.${otherDataType}`)"
        />
      </v-card-text>
    </v-card>

    <v-card class="w-25">
      <v-card-text>
        <div class="d-flex justify-space-between align-center mb-4">
          <div class="subtitle1">
            {{ $t("endpointDataGovernance.privacySensitiveDataTab.selectedParameters.title") }}
          </div>
          <span
            class="coro-link"
            :class="{ 'coro-link--disabled': actionNotAllowed }"
            data-testid="dlp-set-params-btn"
            @click="onEditClick()"
          >
            {{ $t("general.edit") }}
          </span>
        </div>
        <div class="mb-6">
          <div class="subtitle1 mb-2">
            {{ $t("endpointDataGovernance.privacySensitiveDataTab.selectedParameters.industries") }}
          </div>
          <div
            v-for="industry in modelValue?.wizardParameters.industries.slice().sort()"
            :key="industry"
          >
            <span>{{ $t(`modals.setDataGovernanceIndustry.industries.${industry}`) }}</span>
          </div>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { type PrivacySensitiveDataSettings } from "@/_store/endpoint-data-governance/privacy-sensitive-data.module.ts";

export default defineComponent({
  props: {
    modelValue: {
      type: Object as PropType<PrivacySensitiveDataSettings>,
      required: true,
    },
    recommendedDataTypes: {
      type: Array as PropType<string[]>,
      required: true,
    },
    otherDataTypes: {
      type: Array as PropType<string[]>,
      required: true,
    },
    actionNotAllowed: {
      type: Boolean,
      required: true,
    },
  },
  emits: ["update:modelValue", "setParameters"],
  setup(props, { emit }) {
    const localValue = computed({
      get: () => props.modelValue,
      set: (value) => emit("update:modelValue", value),
    });

    const onEditClick = () => {
      if (props.actionNotAllowed) return;
      emit("setParameters");
    };

    return {
      localValue,
      onEditClick,
    };
  },
});
</script>

<style scoped>
.sensitive-data-wrapper {
  flex-grow: 1;
}
</style>
